/**
 * Calendar Page — Elvoaq Professional Business Calendar
 * Full-featured: sidebar mini-cal, categories, month/week/day/year views,
 * search, drag-and-drop, event popup, keyboard shortcuts.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faPlus,
  faSearch,
  faTimes,
  faMapMarkerAlt,
  faTrash,
  faTrashAlt,
  faUndoAlt,
  faBars,

} from '@fortawesome/free-solid-svg-icons';
import useI18n from '../hooks/useI18n';
import { useCalendarEvents, CalendarEvent } from '../hooks/useCalendarEvents';
import CalendarEventModal from '../components/CalendarEventModal';
import '../styles/Calendar.css';

type ViewMode = 'day' | 'week' | 'month' | 'year';

/* ──────────── helpers ──────────── */
const fmtDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const offsetDate = (d: Date, days: number) => {
  const n = new Date(d);
  n.setDate(n.getDate() + days);
  return n;
};

const getWeekStartSunday = (d: Date) => {
  const s = new Date(d);
  s.setDate(s.getDate() - s.getDay());
  return s;
};

/* ──────────── Component ──────────── */
const CalendarPage: React.FC = () => {
  const { t } = useI18n();
  const {
    categories,
    addEvent,
    updateEvent,
    deleteEvent,
    loadTrashedEvents,
    restoreEvent,
    permanentDeleteEvent,
    trashedEvents,
    trashLoading,
    moveEvent,
    addCategory,
    deleteCategory,
    toggleCategoryVisibility,
    getVisibleEvents,
    getEventsForDate,
    searchEvents,
    getCategoryById,
  } = useCalendarEvents();

  /* ── nav state ── */
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<ViewMode>('month');
  const [miniDate, setMiniDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  /* ── modal state ── */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [initialDate, setInitialDate] = useState('');

  /* ── popup state ── */
  const [popupEvent, setPopupEvent] = useState<CalendarEvent | null>(null);
  const [popupPos, setPopupPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const popupRef = useRef<HTMLDivElement>(null);

  /* ── search state ── */
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  /* ── trash state ── */
  const [showTrash, setShowTrash] = useState(false);

  /* ── mobile calendar sidebar state ── */
  const [calSidebarOpen, setCalSidebarOpen] = useState(false);

  function openTrash() {
    setShowTrash(true);
    loadTrashedEvents();
  }

  /* ── add-category state ── */
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState('#7c5cfc');

  const CAT_COLORS = ['#7c5cfc', '#ff4d6a', '#00d4aa', '#ff8c42', '#4da6ff', '#ff6eb4', '#ffd166', '#a8ff78', '#6366f1', '#e11d48'];

  /* ── i18n helpers ── */
  const monthNames = useCallback(() => [
    t('calendar.january'), t('calendar.february'), t('calendar.march'),
    t('calendar.april'), t('calendar.may'), t('calendar.june'),
    t('calendar.july'), t('calendar.august'), t('calendar.september'),
    t('calendar.october'), t('calendar.november'), t('calendar.december'),
  ], [t]);

  const shortMonths = useCallback(() => {
    return monthNames().map(m => m.substring(0, 3));
  }, [monthNames]);

  const dayLabels = useCallback(() => [
    t('calendar.sun'), t('calendar.mon'), t('calendar.tue'),
    t('calendar.wed'), t('calendar.thu'), t('calendar.fri'), t('calendar.sat'),
  ], [t]);

  const fullDayNames = useCallback(() => [
    t('calendar.sunday'), t('calendar.monday'), t('calendar.tuesday'),
    t('calendar.wednesday'), t('calendar.thursday'), t('calendar.friday'), t('calendar.saturday'),
  ], [t]);

  const today = new Date();
  const todayStr = fmtDate(today);

  /* ── title ── */
  const getTitle = useCallback(() => {
    const mn = monthNames();
    if (currentView === 'month') return `${mn[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    if (currentView === 'year') return `${currentDate.getFullYear()}`;
    if (currentView === 'day') return `${mn[currentDate.getMonth()]} ${currentDate.getDate()}, ${currentDate.getFullYear()}`;
    // week
    const start = getWeekStartSunday(currentDate);
    const end = offsetDate(start, 6);
    const sm = mn[start.getMonth()];
    const em = mn[end.getMonth()];
    if (start.getMonth() === end.getMonth()) {
      return `${sm} ${start.getDate()} – ${end.getDate()}, ${end.getFullYear()}`;
    }
    return `${sm} ${start.getDate()} – ${em} ${end.getDate()}, ${end.getFullYear()}`;
  }, [currentDate, currentView, monthNames]);

  /* ── navigation ── */
  const navigate = useCallback((dir: number) => {
    setCurrentDate(prev => {
      const d = new Date(prev);
      if (currentView === 'month') { d.setMonth(d.getMonth() + dir); }
      else if (currentView === 'year') { d.setFullYear(d.getFullYear() + dir); }
      else if (currentView === 'week') { d.setDate(d.getDate() + dir * 7); }
      else { d.setDate(d.getDate() + dir); }
      return d;
    });
  }, [currentView]);

  const goToday = useCallback(() => {
    const t = new Date();
    setCurrentDate(t);
    setMiniDate(new Date(t));
    setSelectedDate(new Date(t));
  }, []);

  const setView = useCallback((v: ViewMode) => {
    setCurrentView(v);
  }, []);

  /* ── modal helpers ── */
  const openCreateModal = useCallback((dateStr?: string) => {
    setEditingEvent(null);
    setInitialDate(dateStr || fmtDate(new Date()));
    setIsModalOpen(true);
    setPopupEvent(null);
  }, []);

  const openEditModal = useCallback((ev: CalendarEvent) => {
    setEditingEvent(ev);
    setInitialDate(ev.date);
    setIsModalOpen(true);
    setPopupEvent(null);
  }, []);

  const handleSave = useCallback((data: Omit<CalendarEvent, 'id' | 'createdAt'>) => {
    if (editingEvent) {
      updateEvent(editingEvent.id, data);
    } else {
      addEvent(data);
    }
    setIsModalOpen(false);
  }, [editingEvent, updateEvent, addEvent]);

  const handleDelete = useCallback((id: string) => {
    deleteEvent(id);
    setIsModalOpen(false);
    setPopupEvent(null);
  }, [deleteEvent]);

  /* ── popup ── */
  const openPopup = useCallback((ev: CalendarEvent, trigger: HTMLElement) => {
    const rect = trigger.getBoundingClientRect();
    let left = rect.right + 8;
    let top = rect.top;
    if (left + 280 > window.innerWidth) left = rect.left - 288;
    if (top + 200 > window.innerHeight) top = window.innerHeight - 220;
    setPopupEvent(ev);
    setPopupPos({ left, top });
  }, []);

  // Close popup on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popupEvent && popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setPopupEvent(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popupEvent]);

  /* ── search ── */
  useEffect(() => {
    if (showSearch && searchInputRef.current) searchInputRef.current.focus();
  }, [showSearch]);

  const searchResults = searchQuery ? searchEvents(searchQuery) : [];

  /* ── keyboard shortcuts ── */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isModalOpen || showSearch) {
        if (e.key === 'Escape') {
          if (showSearch) { setShowSearch(false); setSearchQuery(''); }
          if (isModalOpen) setIsModalOpen(false);
        }
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
          e.preventDefault();
          setShowSearch(true);
        }
        return;
      }
      if (e.key === 'ArrowLeft') navigate(-1);
      if (e.key === 'ArrowRight') navigate(1);
      if (e.key === 'd') setView('day');
      if (e.key === 'w') setView('week');
      if (e.key === 'm') setView('month');
      if (e.key === 'y') setView('year');
      if (e.key === 't') goToday();
      if (e.key === 'n') openCreateModal();
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isModalOpen, showSearch, navigate, setView, goToday, openCreateModal]);

  /* ── add category ── */
  const handleAddCategory = () => {
    if (!newCatName.trim()) return;
    addCategory(newCatName.trim(), newCatColor);
    setNewCatName('');
    setNewCatColor('#7c5cfc');
    setShowAddCategory(false);
  };

  /* ── get translated category name ── */
  const getCatDisplayName = useCallback((cat: { id: string; name: string; isDefault?: boolean }) => {
    if (cat.isDefault) {
      const key = `calendar.cat.${cat.id}`;
      const translated = t(key);
      return translated !== key ? translated : cat.name;
    }
    return cat.name;
  }, [t]);

  /* ═════════════════════════ MINI CALENDAR ═════════════════════════ */
  const renderMiniCalendar = () => {
    const mn = monthNames();
    const dl = dayLabels();
    const first = new Date(miniDate.getFullYear(), miniDate.getMonth(), 1);
    const start = new Date(first);
    start.setDate(start.getDate() - start.getDay());

    const eventDates = new Set(getVisibleEvents().map(e => e.date));
    const cells: React.ReactNode[] = [];

    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const ds = fmtDate(d);
      const isOther = d.getMonth() !== miniDate.getMonth();
      const isToday = ds === todayStr;
      const isSel = ds === fmtDate(selectedDate);
      const hasEvent = eventDates.has(ds);

      cells.push(
        <button
          key={i}
          className={`ecal-mini-cell${isOther ? ' other-month' : ''}${isToday ? ' today' : ''}${isSel ? ' selected' : ''}${hasEvent ? ' has-event' : ''}`}
          onClick={() => {
            setSelectedDate(new Date(d));
            setCurrentDate(new Date(d));
            if (currentView === 'month') setMiniDate(new Date(d));
          }}
        >
          {d.getDate()}
        </button>
      );
    }

    return (
      <div className="ecal-mini-calendar">
        <div className="ecal-mini-header">
          <div className="ecal-mini-title">{mn[miniDate.getMonth()]} {miniDate.getFullYear()}</div>
          <div className="ecal-mini-nav">
            <button className="ecal-mini-nav-btn" onClick={() => setMiniDate(prev => { const d = new Date(prev); d.setMonth(d.getMonth() - 1); return d; })}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button className="ecal-mini-nav-btn" onClick={() => setMiniDate(prev => { const d = new Date(prev); d.setMonth(d.getMonth() + 1); return d; })}>
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
        <div className="ecal-mini-days-row">
          {dl.map(l => <div key={l} className="ecal-mini-day-label">{l.charAt(0)}</div>)}
        </div>
        <div className="ecal-mini-grid">{cells}</div>
      </div>
    );
  };

  /* ═════════════════════════ SIDEBAR CATEGORIES ═════════════════════════ */
  const renderCategories = () => (
    <div className="ecal-sidebar-section">
      <div className="ecal-sidebar-section-title">
        <span>{t('calendar.myCalendars')}</span>
        <button className="ecal-add-cat-btn" onClick={() => setShowAddCategory(!showAddCategory)} title={t('calendar.addCategory')}>
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>

      {showAddCategory && (
        <div className="ecal-add-cat-form">
          <input
            value={newCatName}
            onChange={e => setNewCatName(e.target.value)}
            placeholder={t('calendar.categoryName')}
            onKeyDown={e => { if (e.key === 'Enter') handleAddCategory(); }}
            autoFocus
          />
          <div className="ecal-add-cat-colors">
            {CAT_COLORS.map(c => (
              <button
                key={c}
                className={`ecal-add-cat-color${newCatColor === c ? ' selected' : ''}`}
                style={{ backgroundColor: c }}
                onClick={() => setNewCatColor(c)}
              />
            ))}
          </div>
          <div className="ecal-add-cat-actions">
            <button className="ecal-add-cat-save" onClick={handleAddCategory}>{t('calendar.save')}</button>
            <button className="ecal-add-cat-cancel-btn" onClick={() => { setShowAddCategory(false); setNewCatName(''); }}>{t('calendar.cancel')}</button>
          </div>
        </div>
      )}

      {categories.map(cat => (
        <div key={cat.id} className="ecal-calendar-item">
          <div className="ecal-cal-dot" style={{ background: cat.color }} />
          <div className="ecal-cal-name">{getCatDisplayName(cat)}</div>
          {!cat.isDefault && (
            <button
              className="ecal-cal-delete-btn"
              onClick={(e) => { e.stopPropagation(); deleteCategory(cat.id); }}
              title={t('calendar.delete')}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
          <button
            className={`ecal-cal-toggle${cat.visible ? ' checked' : ''}`}
            style={cat.visible ? { background: cat.color } : undefined}
            onClick={() => toggleCategoryVisibility(cat.id)}
          >
            {cat.visible ? '✓' : ''}
          </button>
        </div>
      ))}
    </div>
  );

  /* ═════════════════════════ UPCOMING ═════════════════════════ */
  const renderUpcoming = () => {
    const upcoming = getVisibleEvents()
      .filter(e => e.date >= todayStr)
      .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime))
      .slice(0, 6);

    const sm = shortMonths();

    return (
      <div className="ecal-upcoming-list">
        <div className="ecal-sidebar-section-title">{t('calendar.upcoming')}</div>
        {upcoming.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--cal-text3)', padding: '8px 0' }}>{t('calendar.noEvents')}</div>
        )}
        {upcoming.map(ev => {
          const d = new Date(ev.date + 'T00:00:00');
          return (
            <div
              key={ev.id}
              className="ecal-upcoming-event"
              style={{ borderLeftColor: ev.color }}
              onClick={e => openPopup(ev, e.currentTarget)}
            >
              <div className="ecal-upcoming-event-title">{ev.title}</div>
              <div className="ecal-upcoming-event-time">
                {sm[d.getMonth()]} {d.getDate()} · {ev.allDay ? t('calendar.allDay') : `${ev.startTime} – ${ev.endTime}`}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  /* ═════════════════════════ MONTH VIEW ═════════════════════════ */
  const renderMonthView = () => {
    const dl = dayLabels();
    const first = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const start = new Date(first);
    start.setDate(start.getDate() - start.getDay());

    const cells: React.ReactNode[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const ds = fmtDate(d);
      const isOther = d.getMonth() !== currentDate.getMonth();
      const isToday = ds === todayStr;
      const isSel = ds === fmtDate(selectedDate);
      const dayEvents = getEventsForDate(ds);

      cells.push(
        <div
          key={i}
          className={`ecal-month-cell${isOther ? ' other-month' : ''}${isToday ? ' today' : ''}${isSel ? ' selected' : ''}`}
          data-date={ds}
          onClick={() => { setSelectedDate(new Date(d)); openCreateModal(ds); }}
          onDragOver={e => { e.preventDefault(); (e.currentTarget as HTMLDivElement).classList.add('drag-over'); }}
          onDragLeave={e => { (e.currentTarget as HTMLDivElement).classList.remove('drag-over'); }}
          onDrop={e => {
            e.preventDefault();
            (e.currentTarget as HTMLDivElement).classList.remove('drag-over');
            const evId = e.dataTransfer.getData('text/plain');
            if (evId) moveEvent(evId, ds);
          }}
        >
          <div className="ecal-cell-day">{d.getDate()}</div>
          {dayEvents.slice(0, 3).map(ev => (
            <div
              key={ev.id}
              className="ecal-event-pill"
              style={{ background: ev.color + '33', color: ev.color }}
              draggable
              onDragStart={e => { e.dataTransfer.setData('text/plain', ev.id); e.stopPropagation(); }}
              onClick={e => { e.stopPropagation(); openPopup(ev, e.currentTarget); }}
            >
              {ev.title}
            </div>
          ))}
          {dayEvents.length > 3 && (
            <div
              className="ecal-more-events"
              onClick={e => { e.stopPropagation(); setCurrentDate(new Date(d)); setView('day'); }}
            >
              +{dayEvents.length - 3} {t('calendar.more')}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="ecal-month-view">
        <div className="ecal-weekday-headers">
          {dl.map(d => <div key={d} className="ecal-weekday-header">{d}</div>)}
        </div>
        <div className="ecal-month-grid">{cells}</div>
      </div>
    );
  };

  /* ═════════════════════════ WEEK VIEW ═════════════════════════ */
  const renderWeekView = () => {
    const dl = dayLabels();
    const weekStart = getWeekStartSunday(currentDate);

    const dayHeaders: React.ReactNode[] = [<div key="corner" className="ecal-week-times" />];
    const dayColumns: React.ReactNode[] = [];

    for (let i = 0; i < 7; i++) {
      const d = offsetDate(weekStart, i);
      const ds = fmtDate(d);
      const isToday = ds === todayStr;

      dayHeaders.push(
        <div key={i} className="ecal-week-day-header">
          <div className="ecal-wdh-name">{dl[i]}</div>
          <div className={`ecal-wdh-num${isToday ? ' today-num' : ''}`}>{d.getDate()}</div>
        </div>
      );

      const dayEvents = getEventsForDate(ds);
      const hourLines: React.ReactNode[] = [];
      for (let h = 0; h < 24; h++) {
        hourLines.push(
          <div key={h} className="ecal-week-hour-line" onClick={() => openCreateModal(ds)} />
        );
      }

      // Current time line
      let timeLine: React.ReactNode = null;
      if (isToday) {
        const now = new Date();
        const pct = (now.getHours() * 60 + now.getMinutes()) / (24 * 60);
        timeLine = <div className="ecal-current-time-line" style={{ top: `${pct * 1440}px` }} />;
      }

      dayColumns.push(
        <div key={i} className="ecal-week-col">
          {hourLines}
          {timeLine}
          {dayEvents.filter(e => !e.allDay).map(ev => {
            const [sh, sm] = ev.startTime.split(':').map(Number);
            const [eh, em] = ev.endTime.split(':').map(Number);
            const top = sh * 60 + sm;
            const height = Math.max((eh * 60 + em) - top, 22);
            return (
              <div
                key={ev.id}
                className="ecal-week-event"
                style={{
                  top: `${top}px`,
                  height: `${height}px`,
                  background: ev.color + '33',
                  color: ev.color,
                  borderLeft: `3px solid ${ev.color}`,
                }}
                onClick={e => { e.stopPropagation(); openPopup(ev, e.currentTarget); }}
              >
                <span className="ecal-week-event-title">{ev.title}</span>
                <span className="ecal-week-event-time">{ev.startTime} – {ev.endTime}</span>
              </div>
            );
          })}
        </div>
      );
    }

    // Time gutter
    const timeSlots: React.ReactNode[] = [];
    for (let h = 0; h < 24; h++) {
      const label = h === 0 ? '' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
      timeSlots.push(<div key={h} className="ecal-week-time-slot">{label}</div>);
    }

    return (
      <div className="ecal-week-view">
        <div className="ecal-week-header">{dayHeaders}</div>
        <div className="ecal-week-body">
          <div className="ecal-week-times">{timeSlots}</div>
          <div className="ecal-week-columns">{dayColumns}</div>
        </div>
      </div>
    );
  };

  /* ═════════════════════════ DAY VIEW ═════════════════════════ */
  const renderDayView = () => {
    const ds = fmtDate(currentDate);
    const dayEvents = getEventsForDate(ds);
    const isToday = ds === todayStr;
    const mn = monthNames();
    const fdn = fullDayNames();

    const timeSlots: React.ReactNode[] = [];
    for (let h = 0; h < 24; h++) {
      const label = h === 0 ? '' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
      timeSlots.push(<div key={h} className="ecal-week-time-slot">{label}</div>);
    }

    const hourBlocks: React.ReactNode[] = [];
    for (let h = 0; h < 24; h++) {
      hourBlocks.push(
        <div
          key={h}
          className="ecal-day-hour-block"
          onClick={() => openCreateModal(ds)}
        />
      );
    }

    return (
      <div className="ecal-day-view">
        <div className="ecal-day-header">
          <h2 className="ecal-day-header-title">
            {fdn[currentDate.getDay()]}, {mn[currentDate.getMonth()]} {currentDate.getDate()}
          </h2>
          <div className="ecal-day-header-sub">{isToday ? `🟢 ${t('calendar.today')}` : String(currentDate.getFullYear())}</div>
        </div>
        <div className="ecal-day-body">
          <div className="ecal-day-times ecal-week-times">{timeSlots}</div>
          <div className="ecal-day-events-col">
            {hourBlocks}
            {isToday && (() => {
              const now = new Date();
              const pct = (now.getHours() * 60 + now.getMinutes()) / (24 * 60);
              return <div className="ecal-current-time-line" style={{ top: `${pct * 1440}px` }} />;
            })()}
            {dayEvents.filter(e => !e.allDay).map(ev => {
              const [sh, sm] = ev.startTime.split(':').map(Number);
              const [eh, em] = ev.endTime.split(':').map(Number);
              const top = sh * 60 + sm;
              const height = Math.max((eh * 60 + em) - top, 30);
              return (
                <div
                  key={ev.id}
                  className="ecal-day-event"
                  style={{
                    top: `${top}px`,
                    height: `${height}px`,
                    background: ev.color + '22',
                    borderLeft: `4px solid ${ev.color}`,
                  }}
                  onClick={e => { e.stopPropagation(); openPopup(ev, e.currentTarget); }}
                >
                  <div className="ecal-day-event-title" style={{ color: ev.color }}>{ev.title}</div>
                  <div className="ecal-day-event-time">
                    {ev.startTime} – {ev.endTime}{ev.location ? ` · ${ev.location}` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  /* ═════════════════════════ YEAR VIEW ═════════════════════════ */
  const renderYearView = () => {
    const mn = monthNames();
    const dl = dayLabels();
    const eventDates = new Set(getVisibleEvents().map(e => e.date));

    return (
      <div className="ecal-year-view">
        {Array.from({ length: 12 }, (_, m) => {
          const first = new Date(currentDate.getFullYear(), m, 1);
          const start = new Date(first);
          start.setDate(start.getDate() - start.getDay());

          const cells: React.ReactNode[] = [];
          for (let i = 0; i < 35; i++) {
            const d = new Date(start);
            d.setDate(d.getDate() + i);
            const ds = fmtDate(d);
            const isOther = d.getMonth() !== m;
            const isToday = ds === todayStr;
            const hasEvent = eventDates.has(ds);

            cells.push(
              <button
                key={i}
                className={`ecal-ymc-cell${isOther ? ' other-month' : ''}${isToday ? ' today' : ''}${hasEvent ? ' has-event' : ''}`}
              >
                {d.getDate()}
              </button>
            );
          }

          return (
            <div
              key={m}
              className="ecal-year-month-card"
              onClick={() => { setCurrentDate(new Date(currentDate.getFullYear(), m, 1)); setMiniDate(new Date(currentDate.getFullYear(), m, 1)); setView('month'); }}
            >
              <div className="ecal-ymc-title">{mn[m]}</div>
              <div className="ecal-ymc-grid">
                {dl.map(l => <div key={l} className="ecal-ymc-day-label">{l.charAt(0)}</div>)}
                {cells}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  /* ═════════════════════════ SEARCH PANEL ═════════════════════════ */
  const renderSearchPanel = () => {
    if (!showSearch) return null;
    const sm = shortMonths();
    return (
      <div className="ecal-search-panel" onClick={() => { setShowSearch(false); setSearchQuery(''); }}>
        <div className="ecal-search-box" onClick={e => e.stopPropagation()}>
          <div className="ecal-search-input-wrap">
            <span className="ecal-search-icon"><FontAwesomeIcon icon={faSearch} /></span>
            <input
              ref={searchInputRef}
              className="ecal-search-input"
              type="text"
              placeholder={t('calendar.searchEvents')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <button className="ecal-search-close" onClick={() => { setShowSearch(false); setSearchQuery(''); }}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className="ecal-search-results">
            {searchQuery && searchResults.length === 0 && (
              <div className="ecal-search-empty">{t('calendar.noEvents')}</div>
            )}
            {searchResults.map(ev => {
              const d = new Date(ev.date + 'T00:00:00');
              return (
                <div
                  key={ev.id}
                  className="ecal-search-result"
                  onClick={() => {
                    setCurrentDate(new Date(ev.date + 'T00:00:00'));
                    setMiniDate(new Date(ev.date + 'T00:00:00'));
                    setShowSearch(false);
                    setSearchQuery('');
                    setView('day');
                  }}
                >
                  <div className="ecal-sr-dot" style={{ background: ev.color }} />
                  <div className="ecal-sr-info">
                    <div className="ecal-sr-title">{ev.title}</div>
                    <div className="ecal-sr-date">
                      {sm[d.getMonth()]} {d.getDate()}, {d.getFullYear()} · {ev.startTime}{ev.location ? ` · ${ev.location}` : ''}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  /* ═════════════════════════ EVENT POPUP ═════════════════════════ */
  const renderEventPopup = () => {
    if (!popupEvent) return null;
    const cat = getCategoryById(popupEvent.categoryId);
    return (
      <>
        <div className="ecal-popup-overlay" onClick={() => setPopupEvent(null)} />
        <div ref={popupRef} className="ecal-event-popup" style={{ left: popupPos.left, top: popupPos.top }}>
          <div className="ecal-ep-color-bar" style={{ background: popupEvent.color }} />
          <div className="ecal-ep-title">{popupEvent.title}</div>
          <div className="ecal-ep-time">
            {popupEvent.date} · {popupEvent.allDay ? t('calendar.allDay') : `${popupEvent.startTime} – ${popupEvent.endTime}`}
          </div>
          {popupEvent.location && (
            <div className="ecal-ep-location">
              <FontAwesomeIcon icon={faMapMarkerAlt} /> {popupEvent.location}
            </div>
          )}
          {cat && <div className="ecal-ep-category">{getCatDisplayName(cat)}</div>}
          <div className="ecal-ep-actions">
            <button className="ecal-ep-btn" onClick={() => openEditModal(popupEvent)}>{t('calendar.edit')}</button>
            <button
              className="ecal-ep-btn delete"
              onClick={() => {
                deleteEvent(popupEvent.id);
                setPopupEvent(null);
              }}
            >
              <FontAwesomeIcon icon={faTrash} style={{ marginRight: 5 }} />
              {t('calendar.delete')}
            </button>
          </div>
        </div>
      </>
    );
  };

  /* ═════════════════════════ RENDER ═════════════════════════ */
  return (
    <div className="elvoaq-calendar">
      {/* Header */}
      <div className="ecal-header">
        <div className="ecal-header-left">
          <h1 className="ecal-page-title">{t('calendar.title')}</h1>
          <div className="ecal-nav-arrows">
            <button className="ecal-nav-btn" onClick={() => navigate(-1)}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button className="ecal-today-btn" onClick={goToday}>{t('calendar.today')}</button>
            <button className="ecal-nav-btn" onClick={() => navigate(1)}>
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
          <h2 className="ecal-current-title">{getTitle()}</h2>
        </div>
        <div className="ecal-header-right">
          <button className="ecal-sidebar-toggle" onClick={() => setCalSidebarOpen(true)} title="Calendar sidebar">
            <FontAwesomeIcon icon={faBars} />
          </button>
          <button className="ecal-search-btn" onClick={() => setShowSearch(true)} title={t('calendar.searchEvents')}>
            <FontAwesomeIcon icon={faSearch} />
          </button>
          <div className="ecal-view-tabs">
            {(['day', 'week', 'month', 'year'] as ViewMode[]).map(v => (
              <button
                key={v}
                className={`ecal-view-tab${currentView === v ? ' active' : ''}`}
                onClick={() => setView(v)}
              >
                {t(`calendar.${v}`)}
              </button>
            ))}
          </div>
          <button className="ecal-add-btn" onClick={() => openCreateModal()} title={t('calendar.newEvent')}>
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
      </div>

      {/* Main */}
      <div className="ecal-main">
        {/* Calendar Sidebar Backdrop (mobile) */}
        <div
          className={`ecal-sidebar-backdrop${calSidebarOpen ? ' visible' : ''}`}
          onClick={() => setCalSidebarOpen(false)}
        />
        {/* Sidebar */}
        <div className={`ecal-sidebar${calSidebarOpen ? ' mobile-open' : ''}`}>
          {renderMiniCalendar()}
          {renderCategories()}
          {renderUpcoming()}
          {/* Trash link */}
          <div className="ecal-sidebar-section" style={{ paddingTop: 6 }}>
            <button className="ecal-trash-link" onClick={() => { openTrash(); setCalSidebarOpen(false); }}>
              <FontAwesomeIcon icon={faTrashAlt} />
              {t('calendar.trash') || 'Recently Deleted'}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="ecal-content">
          {currentView === 'month' && renderMonthView()}
          {currentView === 'week' && renderWeekView()}
          {currentView === 'day' && renderDayView()}
          {currentView === 'year' && renderYearView()}
        </div>
      </div>

      {/* Event Popup */}
      {renderEventPopup()}

      {/* Search */}
      {renderSearchPanel()}

      {/* Trash Panel */}
      {showTrash && (
        <div className="ecal-trash-overlay" onClick={() => setShowTrash(false)}>
          <div className="ecal-trash-panel" onClick={e => e.stopPropagation()}>
            <div className="ecal-trash-header">
              <div className="ecal-trash-header-left">
                <div className="ecal-trash-icon-wrap">
                  <FontAwesomeIcon icon={faTrashAlt} />
                </div>
                <div>
                  <h3 className="ecal-trash-title">{t('calendar.trash') || 'Recently Deleted'}</h3>
                  <p className="ecal-trash-sub">{t('calendar.trashDesc') || 'Events are permanently removed after 30 days'}</p>
                </div>
              </div>
              <button className="ecal-trash-close" onClick={() => setShowTrash(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {trashLoading ? (
              <div className="ecal-trash-empty">
                <FontAwesomeIcon icon={faTrashAlt} className="ecal-trash-empty-icon" />
                <p>{t('calendar.loading') || 'Loading...'}</p>
              </div>
            ) : trashedEvents.length === 0 ? (
              <div className="ecal-trash-empty">
                <FontAwesomeIcon icon={faTrashAlt} className="ecal-trash-empty-icon" />
                <p>{t('calendar.trashEmpty') || 'No deleted events'}</p>
                <span>{t('calendar.trashEmptyDesc') || 'Deleted events will appear here'}</span>
              </div>
            ) : (
              <div className="ecal-trash-list">
                {trashedEvents.map(ev => {
                  const cat = getCategoryById(ev.categoryId);
                  const d = new Date(ev.date + 'T00:00:00');
                  const sm = shortMonths();
                  return (
                    <div className="ecal-trash-row" key={ev.id}>
                      <div className="ecal-trash-row-color" style={{ background: ev.color }} />
                      <div className="ecal-trash-row-info">
                        <div className="ecal-trash-row-title">{ev.title}</div>
                        <div className="ecal-trash-row-meta">
                          <span>{sm[d.getMonth()]} {d.getDate()}, {d.getFullYear()}</span>
                          {!ev.allDay && <span> · {ev.startTime} – {ev.endTime}</span>}
                          {cat && <span> · {getCatDisplayName(cat)}</span>}
                        </div>
                      </div>
                      <div className="ecal-trash-row-actions">
                        <button
                          className="ecal-trash-restore-btn"
                          onClick={() => restoreEvent(ev.id)}
                          title={t('calendar.restore') || 'Restore'}
                        >
                          <FontAwesomeIcon icon={faUndoAlt} />
                        </button>
                        <button
                          className="ecal-trash-del-btn"
                          onClick={() => permanentDeleteEvent(ev.id)}
                          title={t('calendar.deleteForever') || 'Delete forever'}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      <CalendarEventModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        onDelete={handleDelete}
        initialDate={initialDate}
        event={editingEvent}
        categories={categories}
      />
    </div>
  );
};

export default CalendarPage;
